import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";

const BASE_URL = process.env.REACT_APP_API_URL;

interface Usuario {
  id: number;
  nome: string;
  email: string;
  foto?: {
    id: number;
    nome: string;
    tipo: string;
    url: string;
  };
}

interface AuthContextType {
  user: Usuario | null;
  isLoggedIn: boolean;
  isAgent: boolean;
  isDriver: boolean;
  loading: boolean;
  setUser: (user: Usuario | null) => void;
  setIsLoggedIn: (value: boolean) => void;
  setIsAgent: (value: boolean) => void;
  setIsDriver: (value: boolean) => void;
  refreshUserData: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<Usuario | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAgent, setIsAgent] = useState(false);
  const [isDriver, setIsDriver] = useState(false);
  const [loading, setLoading] = useState(true); // 🔥 novo estado

  const refreshUserData = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      setUser(null);
      setIsLoggedIn(false);
      setIsAgent(false);
      setIsDriver(false);
      setLoading(false);
      return;
    }

    try {
      const userRes = await fetch(`${BASE_URL}/usuarios/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userRes.ok) throw new Error("Erro ao buscar usuário");

      const userData = await userRes.json();
      setUser(userData);
      setIsLoggedIn(true);

      const empresaRes = await fetch(`${BASE_URL}/usuarios/me/empresa`, { headers: { Authorization: `Bearer ${token}` } });
      const motoristaRes = await fetch(`${BASE_URL}/usuarios/me/motorista`, { headers: { Authorization: `Bearer ${token}` } });

      const temEmpresa = empresaRes.ok ? await empresaRes.json() : false;
      const isMotorista = motoristaRes.ok ? await motoristaRes.json() : false;

      setIsAgent(temEmpresa === true);
      setIsDriver(isMotorista === true);
    } catch (err) {
      console.error("Erro ao atualizar dados do usuário:", err);
      setUser(null);
      setIsLoggedIn(false);
      setIsAgent(false);
      setIsDriver(false);
    } finally {
      setLoading(false); // 🔥 garante que terminou
    }
  };

  useEffect(() => {
    refreshUserData();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        isAgent,
        isDriver,
        loading,
        setUser,
        setIsLoggedIn,
        setIsAgent,
        setIsDriver,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return context;
};
